import React, { useState, useCallback, useMemo, useEffect, ReactNode, useRef, useLayoutEffect, isValidElement, cloneElement } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import GridLayout, { Layout } from 'react-grid-layout';
import { DashboardWidget } from './DashboardWidget';
import { WidgetEditor, WidgetCustomization } from './WidgetEditor';
import type { WidgetConfig } from './types';
import { Button } from '@/components/ui/button';
import { MesaLogo } from '@/components/layout/MesaLogo';
import { useDashboardBreakpoint } from '@/hooks/useDashboardBreakpoint';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { RotateCcw, Plus, Pencil, Check, X, ChevronDown, Link2, Link2Off, Layers, ChevronLeft, ChevronRight, LayoutGrid, PanelsTopLeft, Key, Settings2, Palette, Moon, Trash2, EllipsisVertical } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import { requestRuntimeLogToken } from '@/lib/runtimeToken';
import { isAdminAuthenticated } from '@/lib/adminAuth';
import { subscribeDashboardMeta, loadDashboardMeta, loadViewMode, subscribeViewMode, saveViewMode, subscribeDashboardLayout, loadDashboardLayout, saveDashboardLayout } from '@/lib/dashboardStore';
import type { ViewModeState, DashboardLayoutState } from '@/lib/dashboardTypes';
import { cn } from '@/lib/utils';
import { getDefaultSkin, getSkinById, Skin } from '@/lib/skinRegistry';
import type { SkinProps } from '@/lib/skinRegistry';
import type { CycleRing } from '@/lib/cycleTypes';
import 'react-grid-layout/css/styles.css';

interface DashboardProps {
  widgets: WidgetConfig[];
  onRemoveWidget?: (id: string) => void;
  onAddWidget?: () => void;
  onAddWidgetById?: (id: string) => void;
  addableWidgets?: WidgetConfig[];
  onSaveLayout?: () => void;
  storageKey?: string;
  isEditable?: boolean;
  mobilePagerSettingsHeader?: ReactNode;
  onViewModeChange?: (mode: DashboardViewMode) => void;
  currentGregorianDate?: Date;
  onSyncStateChange?: (state: 'all-linked' | 'all-unlinked' | 'partial') => void;
  onSyncModesChange?: (syncModes: Record<string, 'linked' | 'isolated' | 'fixed'>) => void;
  onToggleAllWidgetsRef?: (fn: () => void) => void;
  cycleRings?: CycleRing[];
  hiddenRings?: string[];
  onToggleRing?: (ringId: string) => void;
  onRingsChange?: (next: CycleRing[]) => void;
  onRotationChange?: (dayOfYear: number, fractionOfDay: number) => void;
  onSyncToNow?: () => void;
  cycleDisplayMode?: 'single' | 'multi';
  onCycleDisplayModeChange?: (mode: 'single' | 'multi') => void;
  dashboardViews?: Array<{ id: string; name?: string }>;
  activeViewId?: string;
  onViewIdChange?: (id: string) => void;
  onCreateView?: () => void;
  onDeleteView?: () => void;
  navItems?: Array<{ id: string; label: string; icon: ReactNode; to: string }>;
  onOpenTheme?: () => void;
  firestoreDisabled?: boolean;
  firestoreError?: boolean;
  showTopToolbar?: boolean;
  onSignOut?: () => void;
  isNowLocked?: boolean;
}

const COLS = 12;
const ROW_HEIGHT = 60;
const MOBILE_PAGER_BOTTOM_NAV_HEIGHT_PX = 48; // matches Button h-12 in the Developer mobile bottom nav
const MOBILE_SAFE_AREA_TOP = 'max(env(safe-area-inset-top, 0px), var(--safe-area-top, 0px))';
const MOBILE_SAFE_AREA_BOTTOM = 'max(env(safe-area-inset-bottom, 0px), var(--safe-area-bottom, 0px))';
const BASELINE_WIDGET_IDS = ['grid', 'gregorian-grid', 'timeline', 'agenda', 'reminders'];
const RESERVED_CUSTOMIZATION_IDS = ['__dreamspell_layout_defaults__'];
const DEFAULT_SKIN_ROW_FRACTIONS: [number, number, number] = [0.22, 0.28, 0.5];

type DashboardViewMode = 'skin' | 'pager' | 'grid';
type CalendarDisplay = 'gregorian' | 'day13' | 'moonphase';

// Helper to resolve component values that might be functions
const resolveComponent = (component: ReactNode | ((props?: any) => ReactNode), props?: any): ReactNode => {
  if (typeof component === 'function') {
    return React.createElement(component as React.ComponentType<any>, props ?? {});
  }
  return component;
};

export function Dashboard({ 
  widgets, 
  onRemoveWidget,
  onAddWidget,
  onAddWidgetById,
  addableWidgets = [],
  onSaveLayout,
  storageKey = 'dashboard-layout',
  isEditable = true,
  mobilePagerSettingsHeader,
  onViewModeChange,
  currentGregorianDate,
  onSyncStateChange,
  onSyncModesChange,
  onToggleAllWidgetsRef,
  cycleRings = [],
  hiddenRings = [],
  onToggleRing,
  onRingsChange,
  onRotationChange,
  onSyncToNow,
  cycleDisplayMode = 'multi',
  onCycleDisplayModeChange,
  dashboardViews = [],
  activeViewId,
  onViewIdChange,
  onCreateView,
  onDeleteView,
  navItems = [],
  onOpenTheme,
  firestoreDisabled = false,
  firestoreError = false,
  showTopToolbar = true,
  onSignOut,
  isNowLocked = false,
}: DashboardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const [currentCalendarWidgetIndex, setCurrentCalendarWidgetIndex] = useState(0);
  const [calendarDisplay, setCalendarDisplay] = useState<CalendarDisplay>('gregorian');
  const [viewMode, setViewMode] = useState<DashboardViewMode>('skin');

  const [activeSkin, setActiveSkin] = useState<Skin>(() => getDefaultSkin());
  const [selectedCycleWidgetId, setSelectedCycleWidgetId] = useState<string>(() => {
    const defaultSkin = getDefaultSkin();
    return defaultSkin.defaultCycleId || 'grid';
  });
  const [skinRowFractions, setSkinRowFractions] = useState<[number, number, number]>(DEFAULT_SKIN_ROW_FRACTIONS);

  const dashboardBreakpoint = useDashboardBreakpoint();
  const isMobileLayout = dashboardBreakpoint === 'mobile' || (typeof containerWidth === 'number' && containerWidth > 0 && containerWidth < 520);

  // Detect landscape orientation on mobile/tablet
  const [isLandscape, setIsLandscape] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth > window.innerHeight;
  });

  const showPrivilegedFixedSyncUi = useMemo(() => {
    return isAdminAuthenticated();
  }, [isEditable]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const handleOrientationChange = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
    };

    window.addEventListener('resize', handleOrientationChange);
    window.addEventListener('orientationchange', handleOrientationChange);
    
    return () => {
      window.removeEventListener('resize', handleOrientationChange);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []);

  // Force maximized single widget in landscape on mobile/tablet
  const shouldForceMaximize = isLandscape && dashboardBreakpoint !== 'desktop';

  // Create view-mode-specific storage key so each view has its own layout
  const effectiveStorageKey = useMemo(() => {
    return `${storageKey}-${viewMode}`;
  }, [storageKey, viewMode]);
  const sharedCustomizationsKey = useMemo(() => `${storageKey}-shared`, [storageKey]);

  // Get calendar widgets (grid, gregorian-grid, cycles)
  const calendarWidgetIds = useMemo(() => 
    widgets
      .filter(w => ['grid', 'gregorian-grid', 'cycles'].includes(w.id))
      .map(w => w.id),
    [widgets]
  );

  const handleCycleCalendarWidget = () => {
    if (calendarWidgetIds.length === 0) return;
    setCurrentCalendarWidgetIndex(prev => (prev + 1) % calendarWidgetIds.length);
  };

  const getCurrentCalendarDisplay = useCallback(() => {
    const currentWidgetId = calendarWidgetIds[currentCalendarWidgetIndex];
    if (!currentGregorianDate || isNaN(currentGregorianDate.getTime())) return '';
    
    switch (currentWidgetId) {
      case 'grid':
        return `${format(currentGregorianDate, 'EEEE, MMMM d, yyyy')} • ${format(currentGregorianDate, 'HH:mm:ss')}`;
      case 'gregorian-grid':
        return `${format(currentGregorianDate, 'EEEE, MMMM d, yyyy')}`;
      case 'cycles':
        return 'Interlocking Cycles';
      default:
        return `${format(currentGregorianDate, 'EEEE, MMMM d, yyyy')} • ${format(currentGregorianDate, 'HH:mm:ss')}`;
    }
  }, [currentGregorianDate, calendarWidgetIds, currentCalendarWidgetIndex]);

  // Fallback for containerWidth in mobile grid mode to avoid blank screen and undefined errors
  const effectiveContainerWidth = useMemo(() => {
    if (containerWidth && containerWidth > 0) return containerWidth;
    if (isMobileLayout && viewMode === 'grid') {
      if (typeof window !== 'undefined' && window.innerWidth) {
        // On mobile, fit to available width with padding accounted for
        return Math.max(320, Math.min(window.innerWidth - 32, 520));
      }
      return 360; // default fallback
    }
    // Desktop or pager mode - return full container or default
    return containerWidth || 1200;
  }, [containerWidth, isMobileLayout, viewMode]);
  
  const [mobileStackUniformRows, setMobileStackUniformRows] = useState<number>(10);
  const [editingWidgetId, setEditingWidgetId] = useState<string | null>(null);

  const [isLayoutReady, setIsLayoutReady] = useState(false);
  const [suppressTransitions, setSuppressTransitions] = useState(true);
  const hasLoadedRef = useRef(false);
  
  const [maximizedWidgetId, setMaximizedWidgetId] = useState<string | null>(null);
  const [showDashboardControls, setShowDashboardControls] = useState<boolean>(false);
  const [showWidgetControls, setShowWidgetControls] = useState<boolean>(false);
  const [widgetCustomizations, setWidgetCustomizations] = useState<Record<string, WidgetCustomization>>({});
  const widgetCustomizationsRef = useRef<Record<string, WidgetCustomization>>({});
  const lastSyncStateRef = useRef<'all-linked' | 'all-unlinked' | 'partial' | undefined>();
  
  // Track minimized widgets and their original sizes
  // Remove automatic fullscreen for mobile mode
  // Fullscreen is now triggered by user action only
  const [minimizedWidgets, setMinimizedWidgets] = useState<Set<string>>(new Set());

  // Store original sizes before minimizing
  const [originalSizes, setOriginalSizes] = useState<Record<string, { w: number; h: number }>>({});
  const persistedLayoutRef = useRef<DashboardLayoutState | null>(null);
  const lastSavedLayoutSerializedRef = useRef<string | null>(null);
  const triedSharedCustomizationsRef = useRef(false);
  const persistLayoutRef = useRef<(() => void) | null>(null);
  const persistRetryTimerRef = useRef<number | null>(null);
  const persistRetryCountRef = useRef(0);

  const [mobileActiveWidgetId, setMobileActiveWidgetId] = useState<string | null>(null);
  const mobileTouchStartRef = useRef<{ x: number; y: number } | null>(null);

  // Auto-maximize first widget in landscape on mobile/tablet
  useEffect(() => {
    if (!shouldForceMaximize) {
      if (maximizedWidgetId) setMaximizedWidgetId(null);
      return;
    }

    const visibleWidgets = widgets.filter(w => !minimizedWidgets.has(w.id));
    if (visibleWidgets.length > 0 && !maximizedWidgetId) {
      setMaximizedWidgetId(visibleWidgets[0].id);
    }
  }, [shouldForceMaximize, widgets, minimizedWidgets, maximizedWidgetId]);


  // Desktop pager: show a tools/details sidebar when viewing single-widget pager on desktop
  const [desktopPagerSidebarOpen, setDesktopPagerSidebarOpen] = useState<boolean>(true);
  
  // Slider scroll ref for programmatic scrolling
  const sliderScrollRef = useRef<HTMLDivElement>(null);

  // Mode-specific widget visibility tracking
  const [skinHiddenWidgets, setSkinHiddenWidgets] = useState<Set<string>>(new Set());

  const [pagerHiddenWidgets, setPagerHiddenWidgets] = useState<Set<string>>(new Set());

  const [gridHiddenWidgets, setGridHiddenWidgets] = useState<Set<string>>(new Set());

  const DEFAULT_SKIN_ROW_ORDER: ('short' | 'medium' | 'tall')[] = ['short', 'medium', 'tall'];
  const [skinRowOrder, setSkinRowOrder] = useState<('short' | 'medium' | 'tall')[]>(DEFAULT_SKIN_ROW_ORDER);

  const viewModeLoadedRef = useRef(false);
  const lastSavedViewModeSerializedRef = useRef<string | null>(null);
  const persistViewModeRef = useRef<(() => void) | null>(null);
  const persistViewModeRetryTimerRef = useRef<number | null>(null);
  const persistViewModeRetryCountRef = useRef(0);

  const setsEqual = useCallback((current: Set<string>, next: string[]) => {
    if (current.size !== next.length) return false;
    for (const value of next) {
      if (!current.has(value)) return false;
    }
    return true;
  }, []);

  const normalizeSkinRowFractions = useCallback((value: unknown): [number, number, number] => {
    if (!Array.isArray(value) || value.length !== 3) {
      return DEFAULT_SKIN_ROW_FRACTIONS;
    }

    const numeric = value.map((entry) => (typeof entry === 'number' && Number.isFinite(entry) ? entry : NaN));
    if (numeric.some((entry) => !Number.isFinite(entry) || entry <= 0)) {
      return DEFAULT_SKIN_ROW_FRACTIONS;
    }

    const total = numeric[0] + numeric[1] + numeric[2];
    if (!Number.isFinite(total) || total <= 0) {
      return DEFAULT_SKIN_ROW_FRACTIONS;
    }

    return [
      numeric[0] / total,
      numeric[1] / total,
      numeric[2] / total,
    ];
  }, []);

  const clearPersistRetry = useCallback(() => {
    if (persistRetryTimerRef.current !== null && typeof window !== 'undefined') {
      window.clearTimeout(persistRetryTimerRef.current);
    }
    persistRetryTimerRef.current = null;
    persistRetryCountRef.current = 0;
  }, []);

  const schedulePersistRetry = useCallback(() => {
    if (typeof window === 'undefined') return;
    if (persistRetryTimerRef.current !== null) return;
    if (persistRetryCountRef.current >= 3) return;

    persistRetryCountRef.current += 1;
    const delayMs = 300 * persistRetryCountRef.current;
    persistRetryTimerRef.current = window.setTimeout(() => {
      persistRetryTimerRef.current = null;
      persistLayoutRef.current?.();
    }, delayMs);
  }, []);

  useEffect(() => {
    return () => {
      if (persistRetryTimerRef.current !== null && typeof window !== 'undefined') {
        window.clearTimeout(persistRetryTimerRef.current);
      }
    };
  }, []);

  // Get current mode's hidden widgets
  const modeHiddenWidgets = useMemo(() => {
    switch (viewMode) {
      case 'skin': return skinHiddenWidgets;
      case 'pager': return pagerHiddenWidgets;
      case 'grid': return gridHiddenWidgets;
      default: return new Set<string>();
    }
  }, [viewMode, skinHiddenWidgets, pagerHiddenWidgets, gridHiddenWidgets]);

  // Menu options: hidden-in-mode + addable supplied by parent (not yet visible)
  const modeAddableWidgets = useMemo(() => {
    const hiddenEntries = widgets
      .filter((w) => modeHiddenWidgets.has(w.id))
      .map((w) => ({ widget: w, source: 'hidden' as const }));
    const newEntries = (addableWidgets || []).map((w) => ({ widget: w, source: 'new' as const }));
    return [...newEntries, ...hiddenEntries];
  }, [addableWidgets, widgets, modeHiddenWidgets]);

  const clearPersistViewModeRetry = useCallback(() => {
    if (persistViewModeRetryTimerRef.current !== null && typeof window !== 'undefined') {
      window.clearTimeout(persistViewModeRetryTimerRef.current);
    }
    persistViewModeRetryTimerRef.current = null;
    persistViewModeRetryCountRef.current = 0;
  }, []);

  const schedulePersistViewModeRetry = useCallback(() => {
    if (typeof window === 'undefined') return;
    if (persistViewModeRetryTimerRef.current !== null) return;
    if (persistViewModeRetryCountRef.current >= 3) return;

    persistViewModeRetryCountRef.current += 1;
    const delayMs = 300 * persistViewModeRetryCountRef.current;
    persistViewModeRetryTimerRef.current = window.setTimeout(() => {
      persistViewModeRetryTimerRef.current = null;
      persistViewModeRef.current?.();
    }, delayMs);
  }, []);

  useEffect(() => {
    return () => {
      if (persistViewModeRetryTimerRef.current !== null && typeof window !== 'undefined') {
        window.clearTimeout(persistViewModeRetryTimerRef.current);
      }
    };
  }, []);

  // Toggle widget visibility for current mode
  const toggleWidgetVisibility = useCallback((widgetId: string) => {
    if (BASELINE_WIDGET_IDS.includes(widgetId)) {
      toast({
        title: 'Always on',
        description: 'Timeline and calendar grids stay visible for event context.',
      });
      return;
    }

    const newHidden = new Set(modeHiddenWidgets);
    if (newHidden.has(widgetId)) {
      newHidden.delete(widgetId);
    } else {
      newHidden.add(widgetId);
    }

    // Update appropriate mode's hidden set
    switch (viewMode) {
      case 'skin':
        setSkinHiddenWidgets(newHidden);
        break;
      case 'pager':
        setPagerHiddenWidgets(newHidden);
        break;
      case 'grid':
        setGridHiddenWidgets(newHidden);
        break;
    }
  }, [viewMode, modeHiddenWidgets]);

  const handleAddWidgetMenu = useCallback(
    (entry: { widget: WidgetConfig; source: 'hidden' | 'new' }) => {
      if (entry.source === 'new' && onAddWidgetById) {
        onAddWidgetById(entry.widget.id);
        return;
      }
      toggleWidgetVisibility(entry.widget.id);
    },
    [onAddWidgetById, toggleWidgetVisibility]
  );

  const handleAddWidgetFromSkin = useCallback(
    (widgetId: string) => {
      if (!widgetId) return;

      const widgetInCurrentView = widgets.some((widget) => widget.id === widgetId);
      let nextHidden = modeHiddenWidgets;

      if (modeHiddenWidgets.has(widgetId)) {
        nextHidden = new Set(modeHiddenWidgets);
        nextHidden.delete(widgetId);
        switch (viewMode) {
          case 'skin':
            setSkinHiddenWidgets(nextHidden);
            break;
          case 'pager':
            setPagerHiddenWidgets(nextHidden);
            break;
          case 'grid':
            setGridHiddenWidgets(nextHidden);
            break;
        }
        if (widgetInCurrentView) {
          return;
        }
      }

      if (!widgetInCurrentView && onAddWidgetById) {
        onAddWidgetById(widgetId);
        return;
      }

      toggleWidgetVisibility(widgetId);
    },
    [modeHiddenWidgets, onAddWidgetById, toggleWidgetVisibility, viewMode, widgets]
  );

  // Load + subscribe view mode & hidden widgets from Firestore, then persist on change
  useEffect(() => {
    let isActive = true;

    const applyViewModeState = (state: ViewModeState | null) => {
      if (!isActive) return;

      const nextMode: DashboardViewMode = state?.viewMode ?? 'skin';
      const sanitizeHidden = (list: string[]) =>
        Array.from(new Set(list))
          .filter((id) => id && !BASELINE_WIDGET_IDS.includes(id))
          .sort();

      const nextSkinHidden = sanitizeHidden(state?.skinHiddenWidgets ?? []);
      const nextPager = sanitizeHidden(state?.pagerHiddenWidgets ?? []);
      const nextGrid = sanitizeHidden(state?.gridHiddenWidgets ?? []);

      const validRowNames = ['short', 'medium', 'tall'] as const;
      const loadedOrder = (state?.skinRowOrder ?? []).filter((r): r is 'short' | 'medium' | 'tall' => validRowNames.includes(r as any));
      const missingRows = validRowNames.filter(r => !loadedOrder.includes(r));
      const nextRowOrder: ('short' | 'medium' | 'tall')[] = [...loadedOrder, ...missingRows];
      const nextSkinConfig = getSkinById(state?.skinId ?? '') ?? getDefaultSkin();
      const nextSelectedCycleWidgetId =
        typeof state?.selectedCycleWidgetId === 'string' && state.selectedCycleWidgetId.length > 0
          ? state.selectedCycleWidgetId
          : (nextSkinConfig.defaultCycleId || 'grid');
      const nextSkinRowFractions = normalizeSkinRowFractions(state?.skinRowFractions);

      const serialized = JSON.stringify({
        viewMode: nextMode,
        skinHiddenWidgets: nextSkinHidden,
        pagerHiddenWidgets: nextPager,
        gridHiddenWidgets: nextGrid,
        skinId: nextSkinConfig.id,
        selectedCycleWidgetId: nextSelectedCycleWidgetId,
        skinRowOrder: nextRowOrder,
        skinRowFractions: nextSkinRowFractions,
      });

      lastSavedViewModeSerializedRef.current = state ? serialized : null;

      setViewMode((prev) => (prev === nextMode ? prev : nextMode));
      setActiveSkin((prev) => (prev.id === nextSkinConfig.id ? prev : nextSkinConfig));
      setSelectedCycleWidgetId((prev) => (prev === nextSelectedCycleWidgetId ? prev : nextSelectedCycleWidgetId));
      setSkinHiddenWidgets((prev) => (setsEqual(prev, nextSkinHidden) ? prev : new Set(nextSkinHidden)));
      setPagerHiddenWidgets((prev) => (setsEqual(prev, nextPager) ? prev : new Set(nextPager)));
      setGridHiddenWidgets((prev) => (setsEqual(prev, nextGrid) ? prev : new Set(nextGrid)));
      setSkinRowOrder((prev) =>
        prev.length === nextRowOrder.length && prev.every((r, i) => r === nextRowOrder[i]) ? prev : nextRowOrder
      );
      setSkinRowFractions((prev) =>
        prev.every((value, index) => Math.abs(value - nextSkinRowFractions[index]) < 0.0001) ? prev : nextSkinRowFractions
      );

      viewModeLoadedRef.current = true;
    };


    const bootstrap = async () => {
      try {
        const initial = await loadViewMode(storageKey);
        applyViewModeState(initial);
      } catch {
        applyViewModeState(null);
      }
    };
    void bootstrap();

    let unsubscribe: (() => void) | undefined;
    

    try {
      unsubscribe = subscribeViewMode(storageKey, applyViewModeState, () => undefined);
    } catch {
      // fallback if import fails
    }

    return () => {
      isActive = false;
      unsubscribe?.();
    };
  }, [normalizeSkinRowFractions, setsEqual, storageKey]);

  const persistViewMode = useCallback(() => {
    if (!viewModeLoadedRef.current) return;
    const nextSkin = Array.from(new Set(skinHiddenWidgets)).sort();
    const nextPager = Array.from(new Set(pagerHiddenWidgets)).sort();
    const nextGrid = Array.from(new Set(gridHiddenWidgets)).sort();

    const payload: ViewModeState = {
      viewMode,
      skinHiddenWidgets: nextSkin,
      pagerHiddenWidgets: nextPager,
      gridHiddenWidgets: nextGrid,
      skinId: activeSkin.id,
      selectedCycleWidgetId,
      skinRowOrder,
      skinRowFractions,
      updatedAt: new Date().toISOString(),
    };

    const { updatedAt: _ts, ...payloadForComparison } = payload;
    const serialized = JSON.stringify(payloadForComparison);
    if (serialized === lastSavedViewModeSerializedRef.current) return;
    (async () => {
      try {
        await saveViewMode(storageKey, payload);
        lastSavedViewModeSerializedRef.current = serialized;
        clearPersistViewModeRetry();
        console.log('[Dashboard] View mode saved:', storageKey);
      } catch (err) {
        schedulePersistViewModeRetry();
        console.error('[Dashboard] Failed to save view mode:', err);
      }
    })();
  }, [
    activeSkin.id,
    clearPersistViewModeRetry,
    gridHiddenWidgets,
    pagerHiddenWidgets,
    schedulePersistViewModeRetry,
    selectedCycleWidgetId,
    skinHiddenWidgets,
    skinRowFractions,
    skinRowOrder,
    storageKey,
    viewMode,
  ]);

  useEffect(() => {
    persistViewModeRef.current = persistViewMode;
  }, [persistViewMode]);

  useEffect(() => {
    persistViewMode();
  }, [persistViewMode]);

  // Derived constants after all hooks
  const isSkinMode = viewMode === 'skin';
  const isPagerMode = viewMode === 'pager';
  const isCompactPagerLayout = isPagerMode && dashboardBreakpoint !== 'desktop';
  const showPagerBottomNav = useMemo(() => viewMode === 'pager' && dashboardBreakpoint !== 'desktop', [viewMode, dashboardBreakpoint]);
  const shouldShowLayoutSkeleton = !isSkinMode && (!isLayoutReady || (!effectiveContainerWidth && viewMode === 'grid' && isMobileLayout));

  // Notify parent of view mode changes (separate effect to avoid dependency issues)
  const viewModeRef = useRef(viewMode);
  useEffect(() => {
    if (viewModeRef.current !== viewMode) {
      viewModeRef.current = viewMode;
      onViewModeChange?.(viewMode);
    }
  }, [viewMode, onViewModeChange]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!isMobileLayout || !isPagerMode) return;

    const html = document.documentElement;
    const body = document.body;

    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevHtmlOverscroll = (html.style as CSSStyleDeclaration & { overscrollBehavior?: string }).overscrollBehavior;
    const prevBodyOverscroll = (body.style as CSSStyleDeclaration & { overscrollBehavior?: string }).overscrollBehavior;

    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    // Prevent iOS/Chrome overscroll bounce while paging.
    (html.style as CSSStyleDeclaration & { overscrollBehavior?: string }).overscrollBehavior = 'none';
    (body.style as CSSStyleDeclaration & { overscrollBehavior?: string }).overscrollBehavior = 'none';

    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      (html.style as CSSStyleDeclaration & { overscrollBehavior?: string }).overscrollBehavior = prevHtmlOverscroll ?? '';
      (body.style as CSSStyleDeclaration & { overscrollBehavior?: string }).overscrollBehavior = prevBodyOverscroll ?? '';
    };
  }, [isMobileLayout, isPagerMode]);

  // Removed effect that disables editing in pager mode to allow Edit Dashboard in both modes

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!isMobileLayout || isPagerMode) return;

    const computeRows = () => {
      const viewportHeight = window.innerHeight || 800;
      // Aim for a consistent, near-screen-height card in stacked grid mode.
      // Clamp so it stays usable across small/large phones.
      const targetPx = Math.max(360, viewportHeight - 240);
      const rows = Math.max(6, Math.min(14, Math.round(targetPx / ROW_HEIGHT)));
      setMobileStackUniformRows(rows);
    };

    computeRows();
    window.addEventListener('resize', computeRows);
    return () => window.removeEventListener('resize', computeRows);
  }, [isMobileLayout, isPagerMode]);

  // Scroll to active widget in slider mode when selection changes
  useEffect(() => {
    if (!sliderScrollRef.current) return;
    if (!mobileActiveWidgetId) return;
    
    const widgetIndex = widgets.findIndex(w => w.id === mobileActiveWidgetId);
    if (widgetIndex === -1) return;
    
    const scrollContainer = sliderScrollRef.current;
    const containerWidth = scrollContainer.clientWidth;
    const targetScroll = containerWidth * widgetIndex;
    
    scrollContainer.scrollTo({
      left: targetScroll,
      behavior: 'smooth'
    });
  }, [mobileActiveWidgetId, viewMode, widgets]);

  const handleSaveCustomization = useCallback((widgetId: string, customization: WidgetCustomization) => {
    // Update state and trigger persistence via existing effect
    setWidgetCustomizations(prev => {
      const next = {
        ...prev,
        [widgetId]: customization
      };
      return next;
    });
  }, []);

  const handleResetCustomization = useCallback((widgetId: string) => {
    setWidgetCustomizations(prev => {
      const next = { ...prev };
      delete next[widgetId];
      return next;
    });
  }, []);

  const handlePatchWidgetCustomization = useCallback((
    widgetId: string,
    customizationPatch: Partial<WidgetCustomization>,
  ) => {
    setWidgetCustomizations((prev) => {
      const current = prev[widgetId] ?? {};
      const merged: WidgetCustomization = {
        ...current,
        ...customizationPatch,
      };

      const cleaned = Object.fromEntries(
        Object.entries(merged).filter(([, value]) => value !== undefined),
      ) as WidgetCustomization;

      if (Object.keys(cleaned).length === 0) {
        if (!(widgetId in prev)) return prev;
        const next = { ...prev };
        delete next[widgetId];
        return next;
      }

      return {
        ...prev,
        [widgetId]: cleaned,
      };
    });
  }, []);

  const handleSelectCycleWidget = useCallback((widgetId: string) => {
    setSelectedCycleWidgetId(widgetId);
    handlePatchWidgetCustomization('cycles', { selectedCycleWidgetId: widgetId });
  }, [handlePatchWidgetCustomization]);

  // Get effective title for a widget
  const getWidgetTitle = useCallback((widget: WidgetConfig) => {
    return (widgetCustomizations[widget.id]?.title ?? widget.title) ?? 'Widget';
  }, [widgetCustomizations]);

  const pagerWidgets = useMemo(() => {
    if (!isPagerMode) return widgets.filter(w => !pagerHiddenWidgets.has(w.id));

    let filtered = widgets.filter(w => !pagerHiddenWidgets.has(w.id));
    const settingsIndex = filtered.findIndex((widget) => widget.id === 'settings');
    if (settingsIndex <= 0) return filtered;
    const settingsWidget = filtered[settingsIndex];
    return [settingsWidget, ...filtered.slice(0, settingsIndex), ...filtered.slice(settingsIndex + 1)];
  }, [isPagerMode, widgets, pagerHiddenWidgets]);

  const resolvePagerGridVariant = useCallback((widget: WidgetConfig) => {
    if (widget.id === 'settings') return 'tall';
    const custom = widgetCustomizations[widget.id];
    const showShort = custom?.showInShort ?? false;
    const showTall = custom?.showInTall ?? true;
    if (showShort && !showTall) return 'short';
    if (!showShort && showTall) return 'tall';
    const pref = custom?.pagerGridVariant ?? 'auto';
    return pref === 'short' ? 'short' : 'tall';
  }, [widgetCustomizations]);

  useEffect(() => {
    if (dashboardBreakpoint === 'desktop') return;

    const list = isPagerMode ? pagerWidgets : widgets;
    setMobileActiveWidgetId((prev) => {
      if (prev && list.some((widget) => widget.id === prev)) return prev;
      return list[0]?.id ?? null;
    });
  }, [dashboardBreakpoint, isPagerMode, pagerWidgets, widgets]);

  const defaultLayout: Layout[] = useMemo(
    () =>
      widgets.map((widget) => ({
        i: widget.id,
        ...widget.defaultLayout,
        minW: widget.defaultLayout.minW ?? 2,
        minH: widget.defaultLayout.minH ?? 2,
      })),
    [widgets]
  );

  const [layout, setLayout] = useState<Layout[] | null>(null);
  const defaultLayoutRef = useRef(defaultLayout);

  useEffect(() => {
    defaultLayoutRef.current = defaultLayout;
  }, [defaultLayout]);

  const mergeLayoutWithDefaults = useCallback((savedLayout: Layout[] | null, defaults: Layout[]) => {
    const defaultConstraints = new Map(
      defaults.map((item) => [
        item.i,
        {
          minW: item.minW ?? 2,
          minH: item.minH ?? 2,
        },
      ])
    );

    const applyConstraints = (item: Layout) => {
      const constraints = defaultConstraints.get(item.i);
      if (!constraints) return item;
      return {
        ...item,
        minW: constraints.minW,
        minH: constraints.minH,
      };
    };

    if (!savedLayout || !Array.isArray(savedLayout)) {
      return defaults;
    }
    const savedIds = new Set(savedLayout.map((item) => item.i));
    const merged = savedLayout
      .filter((item) => defaultConstraints.has(item.i))
      .map(applyConstraints);
    defaults.forEach((dl) => {
      if (!savedIds.has(dl.i)) {
        merged.push(dl);
      }
    });
    return merged;
  }, []);

  const widgetIdsKey = useMemo(() => {
    const allIds = [
      ...widgets.map((widget) => widget.id),
      ...(addableWidgets ?? []).map((widget) => widget.id),
      ...RESERVED_CUSTOMIZATION_IDS,
    ];
    return Array.from(new Set(allIds)).join('|');
  }, [addableWidgets, widgets]);
  const widgetIdSet = useMemo(() => {
    if (!widgetIdsKey) return new Set<string>();
    return new Set(widgetIdsKey.split('|'));
  }, [widgetIdsKey]);

  const filterLayoutState = useCallback(
    (state: DashboardLayoutState): DashboardLayoutState => ({
      layout: (state.layout ?? []).filter((item) => widgetIdSet.has(item.i)),
      customizations: Object.fromEntries(
        Object.entries(state.customizations ?? {}).filter(([widgetId]) => widgetIdSet.has(widgetId))
      ),
      minimizedWidgets: (state.minimizedWidgets ?? []).filter((widgetId) => widgetIdSet.has(widgetId)),
      originalSizes: Object.fromEntries(
        Object.entries(state.originalSizes ?? {}).filter(([widgetId]) => widgetIdSet.has(widgetId))
      ),
    }),
    [widgetIdSet]
  );

  const calculateSyncState = useCallback((customizations: Record<string, WidgetCustomization>) => {
    if (Object.keys(customizations).length === 0) return 'all-linked';
    const modes = Object.values(customizations).map(c => c.syncMode ?? 'linked');
    const hasLinked = modes.some(m => m === 'linked');
    const hasUnlinked = modes.some(m => m === 'isolated' || m === 'fixed');
    
    if (hasLinked && hasUnlinked) return 'partial';
    if (hasUnlinked) return 'all-unlinked';
    return 'all-linked';
  }, []);

  const buildSyncModes = useCallback(
    (customizations: Record<string, WidgetCustomization>) => {
      const syncModes: Record<string, 'linked' | 'isolated' | 'fixed'> = {};
      widgets.forEach((widget) => {
        syncModes[widget.id] = customizations[widget.id]?.syncMode ?? 'linked';
      });
      return syncModes;
    },
    [widgets]
  );

  const normalizeWidgetCustomizations = useCallback(
    (customizations: Record<string, WidgetCustomization>) => {
      return Object.fromEntries(
        Object.entries(customizations).map(([widgetId, customization]) => {
          if (!customization) return [widgetId, customization];
          const rawSlideRow = (customization as { slideRow?: unknown }).slideRow;
          const normalizedSlideRow =
            rawSlideRow === 0 || rawSlideRow === 1 || rawSlideRow === 2
              ? rawSlideRow
              : rawSlideRow === '0'
                ? 0
                : rawSlideRow === '1'
                  ? 1
                  : rawSlideRow === '2'
                    ? 2
                  : undefined;
          const coercedSlideRow = widgetId === 'settings' && normalizedSlideRow === 1 ? 0 : normalizedSlideRow;
          const inferredShowInShort = typeof customization.showInShort === 'boolean'
            ? customization.showInShort
            : coercedSlideRow === 1;
          const inferredShowInMedium = typeof (customization as { showInMedium?: boolean }).showInMedium === 'boolean'
            ? (customization as { showInMedium?: boolean }).showInMedium
            : coercedSlideRow === 2;
          let inferredShowInTall = typeof customization.showInTall === 'boolean'
            ? customization.showInTall
            : coercedSlideRow === 1 || coercedSlideRow === 2
              ? false
              : true;
          if (widgetId === 'settings') {
            inferredShowInTall = true;
          }
          const rawPagerGridVariant = (customization as { pagerGridVariant?: unknown }).pagerGridVariant;
          const inferredPagerGridVariant = rawPagerGridVariant === 'short' || rawPagerGridVariant === 'tall' || rawPagerGridVariant === 'auto'
            ? rawPagerGridVariant
            : 'auto';
          const nextCustomization: WidgetCustomization = {
            ...customization,
            ...(coercedSlideRow !== undefined ? { slideRow: coercedSlideRow } : {}),
            showInShort: inferredShowInShort,
            showInMedium: inferredShowInMedium,
            showInTall: inferredShowInTall,
            pagerGridVariant: inferredPagerGridVariant,
          };
          if (coercedSlideRow === undefined) {
            delete nextCustomization.slideRow;
          }
          return [widgetId, nextCustomization];
        })
      );
    },
    []
  );

  useEffect(() => {
    widgetCustomizationsRef.current = widgetCustomizations;
    const syncState = calculateSyncState(widgetCustomizations);
    if (lastSyncStateRef.current !== syncState) {
      lastSyncStateRef.current = syncState;
      onSyncStateChange?.(syncState);
    }
  }, [calculateSyncState, widgetCustomizations, onSyncStateChange]);

  // Track previous sync modes to avoid infinite loops
  const lastSyncModesRef = useRef<string>('');
  useEffect(() => {
    if (!onSyncModesChange) return;
    const syncModes = buildSyncModes(widgetCustomizations);
    const serialized = JSON.stringify(syncModes);
    if (lastSyncModesRef.current !== serialized) {
      lastSyncModesRef.current = serialized;
      onSyncModesChange(syncModes);
    }
  }, [buildSyncModes, widgetCustomizations, onSyncModesChange]);

  useEffect(() => {
    let isActive = true;
    hasLoadedRef.current = false;
    triedSharedCustomizationsRef.current = false;
    setIsLayoutReady(false);
    setSuppressTransitions(true);

    const finalizeLayoutState = (options: {
      customizations?: Record<string, WidgetCustomization>;
      minimizedWidgets?: string[];
      originalSizes?: Record<string, { w: number; h: number }>;
    } = {}) => {
      if (!isActive) return;
      setWidgetCustomizations(normalizeWidgetCustomizations(options.customizations ?? {}));
      setMinimizedWidgets(new Set(options.minimizedWidgets ?? []));
      setOriginalSizes(options.originalSizes ?? {});
      hasLoadedRef.current = true;
      setIsLayoutReady(true);
    };

    const loadSharedCustomizationsFallback = async (): Promise<Record<string, WidgetCustomization>> => {
      if (triedSharedCustomizationsRef.current) return {};
      triedSharedCustomizationsRef.current = true;
      try {
        const shared = await loadDashboardLayout(sharedCustomizationsKey);
        return (shared?.customizations ?? {}) as Record<string, WidgetCustomization>;
      } catch {
        return {};
      }
    };

    const applyLayoutState = (data: DashboardLayoutState | null) => {
      if (!isActive) return;
      if (data) {
        const filtered = filterLayoutState(data);
        const dataStr = JSON.stringify(filtered);
        // Prevent overwriting if we just saved this exact data
        if (lastSavedLayoutSerializedRef.current === dataStr) {
          console.log('[Grid] Subscription data matches last save, skipping reapply');
          return;
        }
        console.log('[Grid] Applying layout from subscription:', { layoutLength: filtered.layout?.length });
        persistedLayoutRef.current = filtered;
        lastSavedLayoutSerializedRef.current = dataStr;
        setLayout(mergeLayoutWithDefaults(filtered.layout, defaultLayoutRef.current));
        let customizations = filtered.customizations ?? {};
        if (Object.keys(customizations).length === 0 && Object.keys(widgetCustomizationsRef.current).length > 0) {
          customizations = widgetCustomizationsRef.current;
        }
        finalizeLayoutState({
          customizations,
          minimizedWidgets: filtered.minimizedWidgets ?? [],
          originalSizes: filtered.originalSizes ?? {},
        });
        if (Object.keys(customizations).length === 0 && !triedSharedCustomizationsRef.current) {
          (async () => {
            const sharedCustoms = await loadSharedCustomizationsFallback();
            if (!isActive) return;
            if (Object.keys(sharedCustoms).length > 0) {
              setWidgetCustomizations(normalizeWidgetCustomizations(sharedCustoms));
            }
          })();
        }
      } else {
        persistedLayoutRef.current = null;
        lastSavedLayoutSerializedRef.current = null;
        setLayout(defaultLayoutRef.current);
        (async () => {
          const sharedCustoms = await loadSharedCustomizationsFallback();
          if (!isActive) return;
          finalizeLayoutState({ customizations: sharedCustoms });
        })();
        return;
      }
    };

    let unsubscribe: (() => void) | undefined;


    try {
      unsubscribe = subscribeDashboardLayout(effectiveStorageKey, applyLayoutState);
      if (!unsubscribe) {
        (async () => {
          try {
            const data = await loadDashboardLayout(effectiveStorageKey);
            applyLayoutState(data);
          } catch {
            if (isActive) {
              setLayout(defaultLayoutRef.current);
              hasLoadedRef.current = true;
              setIsLayoutReady(true);
            }
          }
        })();
      }
    } catch {
      // fallback if import fails
      hasLoadedRef.current = true;
      setIsLayoutReady(true);
    }

    return () => {
      isActive = false;
      unsubscribe?.();
    };
  }, [
    effectiveStorageKey,
    mergeLayoutWithDefaults,
    widgetIdSet,
    widgetIdsKey,
    filterLayoutState,
    calculateSyncState,
    onSyncStateChange,
    sharedCustomizationsKey,
    loadDashboardLayout,
    normalizeWidgetCustomizations,
  ]);

  const mergePersistedLayoutState = useCallback(
    (currentState: DashboardLayoutState): DashboardLayoutState => {
      const persisted = persistedLayoutRef.current;
      if (!persisted) return filterLayoutState(currentState);

      const filteredPersisted = filterLayoutState(persisted);
      const filteredCurrent = filterLayoutState(currentState);

      const currentIds = new Set(filteredCurrent.layout.map((item) => item.i));
      const mergedLayout = [
        ...filteredCurrent.layout,
        ...(filteredPersisted.layout ?? []).filter((item) => !currentIds.has(item.i)),
      ];

      const mergedCustomizations = {
        ...(filteredPersisted.customizations ?? {}),
        ...(filteredCurrent.customizations ?? {}),
      };

      const mergedOriginalSizes = {
        ...(filteredPersisted.originalSizes ?? {}),
        ...(filteredCurrent.originalSizes ?? {}),
      };

      const mergedMinimized = Array.from(
        new Set([
          ...(filteredPersisted.minimizedWidgets ?? []),
          ...(filteredCurrent.minimizedWidgets ?? []),
        ])
      );

      return {
        layout: mergedLayout,
        customizations: mergedCustomizations,
        minimizedWidgets: mergedMinimized,
        originalSizes: mergedOriginalSizes,
      };
    },
    [filterLayoutState]
  );

  const serializePersistedLayoutState = useCallback(
    (currentState: DashboardLayoutState): string => JSON.stringify(filterLayoutState(currentState)),
    [filterLayoutState]
  );

  useEffect(() => {
    if (!isLayoutReady || !containerWidth) return;
    const frame = window.requestAnimationFrame(() => {
      setSuppressTransitions(false);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isLayoutReady, containerWidth]);

  // Merge new widgets into layout when widgets change
  useEffect(() => {
    if (!hasLoadedRef.current) return;
    setLayout((prev) => {
      if (!prev) return defaultLayout;
      const merged = mergeLayoutWithDefaults(prev, defaultLayout);
      // Only update if we actually added new widgets
      if (merged.length === prev.length) return prev;
      return merged;
    });
  }, [defaultLayout, mergeLayoutWithDefaults]);

  useEffect(() => {
    if (!isEditable && isEditing) {
      setIsEditing(false);
    }
  }, [isEditable, isEditing]);

  const handleToggleMinimize = useCallback(
    (widgetId: string, minimize: boolean) => {
      const widget = widgets.find((w) => w.id === widgetId);
      if (!widget || !layout) return;

      if (minimize) {
        // Store current size before minimizing
        const currentLayout = layout.find((l) => l.i === widgetId);
        if (currentLayout) {
          setOriginalSizes((prev) => ({
            ...prev,
            [widgetId]: { w: currentLayout.w, h: currentLayout.h },
          }));
        }
        setMinimizedWidgets((prev) => new Set([...prev, widgetId]));
        return;
      }

      // Expand
      setMinimizedWidgets((prev) => {
        const next = new Set(prev);
        next.delete(widgetId);
        return next;
      });

      const original = originalSizes[widgetId];
      if (original) {
        setLayout((prev) =>
          prev
            ? prev.map((item) =>
                item.i === widgetId ? { ...item, w: original.w, h: original.h } : item
              )
            : prev
        );
        setOriginalSizes((prev) => {
          if (!prev[widgetId]) return prev;
          const next = { ...prev };
          delete next[widgetId];
          return next;
        });
      }
    },
    [layout, widgets, originalSizes]
  );

  // Merge layout with minimized sizes (display-only; persisted sizes stay in `layout`)
  const mergedLayout = useMemo(
    () =>
      (layout ?? defaultLayout).map((item) => {
        if (!minimizedWidgets.has(item.i)) return item;
        return {
          ...item,
          minW: 1,
          minH: 1,
          w: 1,
          h: 1,
        };
      }),
    [layout, minimizedWidgets, defaultLayout]
  );

  const displayLayout = useMemo(() => {
    if (!isMobileLayout) return mergedLayout;

    const sorted = [...mergedLayout].sort((a, b) => {
      if (a.y !== b.y) return a.y - b.y;
      if (a.x !== b.x) return a.x - b.x;
      return a.i.localeCompare(b.i);
    });

    let nextY = 0;
    // Only use uniform heights in pager mode
    const useUniformHeights = isPagerMode && !isEditing;
    return sorted.map((item) => {
      const nextH = useUniformHeights && !minimizedWidgets.has(item.i) ? mobileStackUniformRows : item.h;
      const stacked: Layout = {
        ...item,
        x: 0,
        y: nextY,
        w: COLS, // Use full width in mobile (COLS columns)
        minW: COLS,
        ...(useUniformHeights && !minimizedWidgets.has(item.i)
          ? { h: nextH, minH: nextH }
          : {}),
      };
      nextY += Math.max(1, stacked.h);
      return stacked;
    });
  }, [isEditing, isMobileLayout, isPagerMode, mergedLayout, minimizedWidgets, mobileStackUniformRows]);

  const effectiveCols = isMobileLayout ? 1 : COLS;

  // Only update layout on drag/resize stop to avoid constant re-renders during interaction
  const handleLayoutChange = useCallback((_newLayout: Layout[]) => {
    // Don't update on every change - wait for stop events
  }, []);

  const handleLayoutStop = useCallback(
    (updatedLayout: Layout[]) => {
      console.log('[Grid] handleLayoutStop called:', { updatedItemCount: updatedLayout.length, isEditing });
      const widgetIds = new Set(widgets.map((w) => w.id));
      const byId = new Map(
        updatedLayout
          .filter((item) => widgetIds.has(item.i))
          .map((item) => [item.i, item])
      );

      setLayout((prev) => {
        const updated = prev
          ? prev
              .map((item) => {
                const next = byId.get(item.i);
                if (!next) return item;

                const keepSize = minimizedWidgets.has(item.i);

                return {
                  ...item,
                  x: next.x,
                  y: next.y,
                  ...(keepSize ? {} : { w: next.w, h: next.h }),
                };
              })
              .filter((item) => widgetIds.has(item.i))
          : prev;
        
        // Persist immediately after drag/resize stops using the updated layout
        console.log('[Grid] Layout changed:', { wasUpdated: updated !== prev, prevLength: prev?.length, updatedLength: updated?.length });
        if (updated && updated !== prev) {
          try {
            // We need to persist with the updated layout, so do it directly here
            const mergedLayoutState = mergeLayoutWithDefaults(updated, defaultLayoutRef.current);
            const nextLayoutState = mergePersistedLayoutState({
              layout: mergedLayoutState,
              customizations: widgetCustomizations,
              minimizedWidgets: [...minimizedWidgets],
              originalSizes,
            });
            const sharedCustomizationsState: DashboardLayoutState = {
              layout: [],
              customizations: widgetCustomizations,
              minimizedWidgets: [],
              originalSizes: {},
            };
            persistedLayoutRef.current = nextLayoutState;
            (async () => {
              try {
                console.log('[Grid] Saving layout directly to Firestore:', { effectiveStorageKey, layoutItemCount: nextLayoutState.layout?.length });
                await saveDashboardLayout(effectiveStorageKey, nextLayoutState);
                lastSavedLayoutSerializedRef.current = JSON.stringify(nextLayoutState);
                clearPersistRetry();
                await saveDashboardLayout(sharedCustomizationsKey, sharedCustomizationsState).catch((error) => {
                  console.error('[Grid] Shared customization mirror save failed:', error);
                });
                console.log('[Grid] ✓ Layout saved successfully');
              } catch (err) {
                lastSavedLayoutSerializedRef.current = null;
                schedulePersistRetry();
                console.error('[Grid] ✗ Failed to save layout:', err);
              }
            })();
          } catch (err) {
            console.error('[Grid] ✗ Exception:', err);
          }
        }
        
        return updated;
      });
    },
    [clearPersistRetry, isEditing, isEditable, widgets, minimizedWidgets, widgetCustomizations, mergeLayoutWithDefaults, mergePersistedLayoutState, originalSizes, effectiveStorageKey, schedulePersistRetry, sharedCustomizationsKey]
  );

  // Update classes on grid items for resize state (min-size + edge contact)
  useEffect(() => {
    if (!isEditing || !displayLayout) return;

    // Create maps of widget ID to flags
    const minSizeMap = new Map<string, boolean>();
    const edgeMap = new Map<string, { left: boolean; right: boolean; top: boolean; bottom: boolean }>();
    const shrinkMap = new Map<string, { left: boolean; right: boolean; top: boolean; bottom: boolean }>();

    displayLayout.forEach((layoutItem) => {
      const widget = widgets.find((w) => w.id === layoutItem.i);
      if (!widget) return;

      const minW = widget.defaultLayout.minW ?? 2;
      const minH = widget.defaultLayout.minH ?? 2;
      
      const isAtMinSize = layoutItem.w <= minW && layoutItem.h <= minH;
      minSizeMap.set(layoutItem.i, isAtMinSize);

      // Determine whether we can shrink on each edge (width/height above min)
      const canShrinkWidth = layoutItem.w > minW;
      const canShrinkHeight = layoutItem.h > minH;
      shrinkMap.set(layoutItem.i, {
        left: canShrinkWidth,
        right: canShrinkWidth,
        top: canShrinkHeight,
        bottom: canShrinkHeight,
      });

      edgeMap.set(layoutItem.i, {
        left: layoutItem.x === 0,
        right: layoutItem.x + layoutItem.w >= COLS,
        top: layoutItem.y === 0,
        // Grid height is unbounded; keep bottom handles visible unless a hard limit is added
        bottom: false,
      });
    });

    // Apply the class to grid items
    const timer = setTimeout(() => {
      const gridItems = document.querySelectorAll('[data-grid-item-id]');
      gridItems.forEach((item) => {
        const widgetId = item.getAttribute('data-grid-item-id');
        if (!widgetId) return;

        item.classList.toggle('at-min-size', Boolean(minSizeMap.get(widgetId)));

        const edges = edgeMap.get(widgetId);
        item.classList.toggle('edge-left', Boolean(edges?.left));
        item.classList.toggle('edge-right', Boolean(edges?.right));
        item.classList.toggle('edge-top', Boolean(edges?.top));
        item.classList.toggle('edge-bottom', Boolean(edges?.bottom));

        const shrinkable = shrinkMap.get(widgetId);
        item.classList.toggle('shrinkable-left', Boolean(edges?.left && shrinkable?.left));
        item.classList.toggle('shrinkable-right', Boolean(edges?.right && shrinkable?.right));
        item.classList.toggle('shrinkable-top', Boolean(edges?.top && shrinkable?.top));
        item.classList.toggle('shrinkable-bottom', Boolean(edges?.bottom && shrinkable?.bottom));
      });
    }, 0);

    return () => clearTimeout(timer);
  }, [isEditing, displayLayout, widgets]);

  const persistLayoutState = useCallback((nextCustomizations: Record<string, WidgetCustomization>) => {
    if (!isEditable || !hasLoadedRef.current || !layout) return;
    const mergedLayoutState = mergeLayoutWithDefaults(layout, defaultLayoutRef.current);
    const nextLayoutState = mergePersistedLayoutState({
      layout: mergedLayoutState,
      customizations: nextCustomizations,
      minimizedWidgets: [...minimizedWidgets],
      originalSizes,
    });
    const sharedCustomizationsState: DashboardLayoutState = {
      layout: [],
      customizations: nextCustomizations,
      minimizedWidgets: [],
      originalSizes: {},
    };
    persistedLayoutRef.current = nextLayoutState;
    (async () => {
      try {
        await saveDashboardLayout(effectiveStorageKey, nextLayoutState);
        lastSavedLayoutSerializedRef.current = serializePersistedLayoutState(nextLayoutState);
        clearPersistRetry();
        await saveDashboardLayout(sharedCustomizationsKey, sharedCustomizationsState).catch((error) => {
          console.error('[Grid] Shared customization mirror save failed:', error);
        });
      } catch (err) {
        lastSavedLayoutSerializedRef.current = null;
        schedulePersistRetry();
        console.error('[Grid] persistLayoutState save error:', err);
      }
    })();
  }, [
    clearPersistRetry,
    isEditable,
    layout,
    minimizedWidgets,
    originalSizes,
    effectiveStorageKey,
    sharedCustomizationsKey,
    mergeLayoutWithDefaults,
    mergePersistedLayoutState,
    serializePersistedLayoutState,
    schedulePersistRetry,
  ]);  
  
  const persistLayout = useCallback(() => {
    persistLayoutState(widgetCustomizations);
  }, [persistLayoutState, widgetCustomizations]);
  
  // Store in ref for use in callbacks
  useEffect(() => {
    persistLayoutRef.current = persistLayout;
  }, [persistLayout]);

  // Auto-save layout changes (minimized widgets, customizations)
  // Persist immediately so checkbox/toggle interactions are written right away.
  useEffect(() => {
    if (!isEditable || !hasLoadedRef.current || !layout) return;

    const currentLayoutState = mergePersistedLayoutState({
      layout: mergeLayoutWithDefaults(layout, defaultLayoutRef.current),
      customizations: widgetCustomizations,
      minimizedWidgets: Array.from(minimizedWidgets),
      originalSizes,
    });
    const currentSerialized = serializePersistedLayoutState(currentLayoutState);

    if (currentSerialized === lastSavedLayoutSerializedRef.current) return;

    if (persistLayoutRef.current) {
      persistLayoutRef.current();
    }
  }, [
    isEditable,
    layout,
    mergeLayoutWithDefaults,
    mergePersistedLayoutState,
    minimizedWidgets,
    originalSizes,
    serializePersistedLayoutState,
    widgetCustomizations,
  ]);

  const toggleWidgetSyncMode = useCallback((widgetId: string) => {
    setWidgetCustomizations((prev) => {
      const current = prev[widgetId] ?? {};
      const nextMode: 'linked' | 'isolated' = (current.syncMode ?? 'linked') === 'linked' ? 'isolated' : 'linked';
      const updated: Record<string, WidgetCustomization> = {
        ...prev,
        [widgetId]: {
          ...current,
          syncMode: nextMode === 'linked' ? undefined : nextMode,
        },
      };
      onSyncStateChange?.(calculateSyncState(updated));
      return updated;
    });
  }, [calculateSyncState, onSyncStateChange]);

  const toggleAllWidgetsSyncMode = useCallback((linkAll?: boolean) => {
    setWidgetCustomizations((prev) => {
      const currentState = calculateSyncState(prev);
      // If linkAll is explicitly passed, use that; otherwise toggle based on current state
      const shouldLinkAll = linkAll !== undefined ? linkAll : currentState === 'all-linked' ? false : true;
      const targetMode = shouldLinkAll ? 'linked' : 'isolated';
      const updated: Record<string, WidgetCustomization> = {};
      widgets.forEach(widget => {
        const current = prev[widget.id] ?? {};
        updated[widget.id] = {
          ...current,
          syncMode: targetMode === 'linked' ? undefined : targetMode,
        };
      });
      onSyncStateChange?.(calculateSyncState(updated));
      return updated;
    });
  }, [widgets, calculateSyncState, onSyncStateChange]);

  // Expose toggle all function to parent via callback ref
  useEffect(() => {
    onToggleAllWidgetsRef?.(toggleAllWidgetsSyncMode);
  }, [onToggleAllWidgetsRef, toggleAllWidgetsSyncMode]);

  const moreMenu = useMemo(() => {
    const hasItems = navItems.length > 0 || Boolean(onOpenTheme) || Boolean(onSyncToNow);
    if (!hasItems) return null;

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            aria-label="More options"
          >
            <EllipsisVertical className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56 bg-card/90 backdrop-blur border-border/60">
          {navItems.map((item) => (
            <DropdownMenuItem key={item.id} asChild>
              <Link to={item.to} className="flex items-center gap-2">
                {item.icon ? <span className="text-primary">{item.icon}</span> : null}
                <span>{item.label}</span>
              </Link>
            </DropdownMenuItem>
          ))}
          {navItems.length > 0 && (onOpenTheme || onSyncToNow) ? <DropdownMenuSeparator /> : null}
          {onOpenTheme && (
            <DropdownMenuItem onSelect={() => onOpenTheme?.()}>
              Theme settings
            </DropdownMenuItem>
          )}
          {onSyncToNow && (
            <DropdownMenuItem onSelect={() => onSyncToNow?.()}>
              Sync to now
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }, [navItems, onOpenTheme, onSyncToNow]);

  const saveLayout = useCallback(() => {
    setIsEditing(false);
    setShowWidgetControls(false);
    persistLayout();
    onSaveLayout?.();
  }, [persistLayout, onSaveLayout]);

  const resetLayout = useCallback(() => {
    setLayout(defaultLayout);
    setMinimizedWidgets(new Set());
    setOriginalSizes({});
  }, [defaultLayout]);

  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (viewMode !== 'grid') {
      setShowDashboardControls(false);
      setShowWidgetControls(false);
    }
  }, [viewMode]);

  // Measure container width with ResizeObserver to prevent layout flashing.
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateWidth = () => {
      const nextWidth = container.offsetWidth;
      if (nextWidth <= 0) {
        return;
      }
      setContainerWidth(nextWidth);
    };

    updateWidth();

    const observer = new ResizeObserver(() => {
      updateWidth();
    });

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, []);

  const editingWidget = editingWidgetId ? widgets.find(w => w.id === editingWidgetId) : null;

  return (
    <div className="relative flex flex-col h-full min-h-0">
      {isSkinMode && showTopToolbar && onSignOut && (
        <div
          className="absolute right-3 z-40"
          style={{ top: `calc(${MOBILE_SAFE_AREA_TOP} + 0.5rem)` }}
        >
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 px-3 bg-background/70 backdrop-blur border-border/60"
            onClick={onSignOut}
          >
            Sign out
          </Button>
        </div>
      )}

      {/* Unified Dashboard Toolbar - Hide in skin mode - FIXED */}
      {!isSkinMode && showTopToolbar && (
        <div
          className="flex-shrink-0 flex items-center justify-between gap-3 mb-4 flex-wrap px-4 py-3 border-b border-border/40 bg-background/70 backdrop-blur sticky top-0 z-30"
          style={{ paddingTop: MOBILE_SAFE_AREA_TOP }}
        >
          {/* Logo Top Left */}
          <div className="flex items-center flex-shrink-0">
            <MesaLogo className="h-8 w-8" />
          </div>

          {/* Left: View Mode Selector */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* Dashboard Selector - shown when dashboard controls are visible */}
            {showDashboardControls && dashboardViews.length > 0 && onViewIdChange && (
              <>
                <Select value={activeViewId || ''} onValueChange={onViewIdChange}>
                  <SelectTrigger className={cn(
                    "h-8 w-[120px] sm:w-[140px] bg-card/60 backdrop-blur border-border/60 text-xs",
                    (firestoreError || firestoreDisabled) && "border-red-500/70 bg-red-950/20 text-red-400"
                  )}>
                    <SelectValue placeholder="View" />
                  </SelectTrigger>
                  <SelectContent className="bg-card/90 backdrop-blur border-border/60">
                    {dashboardViews.map((view) => (
                      <SelectItem key={view.id} value={view.id || 'unknown'}>
                        {view.name ?? 'Dashboard'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {(onCreateView || onDeleteView) && (
                  <>
                    {onCreateView && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={onCreateView}
                        title="Save as new view"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    )}
                    {onDeleteView && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={onDeleteView}
                        title="Delete view"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="default"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => setShowDashboardControls(false)}
                          title="Hide dashboard controls"
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Hide dashboard controls</TooltipContent>
                    </Tooltip>
                  </>
                )}
              </>
            )}

            {/* View Mode Selector - hide when tools expanded */}
            {!showDashboardControls && !showWidgetControls && (
              <>
                <span className="text-xs text-muted-foreground uppercase tracking-wide hidden sm:inline">View:</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center rounded-lg border border-border/60 bg-card/40 p-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setViewMode('skin')}
                        aria-pressed={isSkinMode}
                    className={cn(
                      "h-7 w-7 rounded-md transition-colors",
                      isSkinMode ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                    )}
                    title="Skin view - Full immersive calendar"
                  >
                    <Moon className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setViewMode('pager')}
                    aria-pressed={isPagerMode}
                    className={cn(
                      "h-7 w-7 rounded-md transition-colors",
                      isPagerMode ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                    )}
                    title="Page view - Focus on one widget"
                  >
                    <PanelsTopLeft className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setViewMode('grid')}
                    aria-pressed={viewMode === 'grid'}
                    className={cn(
                      "h-7 w-7 rounded-md transition-colors",
                      viewMode === 'grid' ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                    )}
                    title="Grid view - All widgets at once"
                  >
                    <LayoutGrid className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <div className="space-y-1">
                  <div className="font-semibold">Dashboard View</div>
                  <div className="text-xs space-y-0.5">
                    <div>🌙 <strong>Skin:</strong> Full immersive calendar</div>
                    <div>📄 <strong>Page:</strong> One widget at a time</div>
                    <div>⚡ <strong>Grid:</strong> All widgets visible</div>
                  </div>
                </div>
              </TooltipContent>
            </Tooltip>
              </>
            )}
          </div>

          {/* Right: Edit Controls + More menu */}
          <div className="flex items-center gap-1.5">
            {onSignOut && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 px-3"
                onClick={onSignOut}
              >
                Sign out
              </Button>
            )}
            {isEditable && (
              <div className="flex items-center gap-1.5">
                {/* Dashboard Controls Toggle - only show when not expanded */}
                {!showDashboardControls && !showWidgetControls && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => {
                          setShowDashboardControls(true);
                          setShowWidgetControls(false);
                          setIsEditing(false);
                        }}
                        title="Show dashboard controls"
                      >
                        <Settings2 className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Show dashboard controls</TooltipContent>
                  </Tooltip>
                )}

                {/* Widget Controls - shown when widget controls are visible */}
                {showWidgetControls && (
                  <div className="flex items-center gap-1.5">
                    {(onAddWidget || onAddWidgetById) && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          {onAddWidgetById ? (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="outline" size="sm" className="h-8 text-xs px-2.5 gap-1">
                                  <Plus className="w-3.5 h-3.5" />
                                  <span>Add Widget</span>
                                  <ChevronDown className="w-3 h-3" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {modeAddableWidgets.length ? (
                                  modeAddableWidgets.map((entry) => (
                                    <DropdownMenuItem
                                      key={`${entry.source}-${entry.widget.id}`}
                                      onSelect={() => handleAddWidgetMenu(entry)}
                                    >
                                      {entry.widget.icon && <span className="mr-2">{entry.widget.icon}</span>}
                                      {entry.widget.title}
                                      {entry.source === 'new' && <span className="ml-2 text-xs text-muted-foreground">New</span>}
                                    </DropdownMenuItem>
                                  ))
                                ) : (
                                  <DropdownMenuItem disabled>No widgets to add</DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          ) : (
                            <Button type="button" variant="outline" size="sm" onClick={onAddWidget} className="h-8 text-xs px-2.5 gap-1">
                              <Plus className="w-3.5 h-3.5" />
                              <span>Add</span>
                            </Button>
                          )}
                        </TooltipTrigger>
                        <TooltipContent>Add widget to dashboard</TooltipContent>
                      </Tooltip>
                    )}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={resetLayout}
                          title="Reset layout to default"
                        >
                          <RotateCcw className="w-3.5 h-3.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Reset to default layout</TooltipContent>
                    </Tooltip>
                  </div>
                )}

                {/* Edit/Done Toggle for Widget Controls - hide when dashboard controls shown */}
                {!showDashboardControls && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={isEditing ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          if (isEditing) {
                            saveLayout();
                          } else {
                            setIsEditing(true);
                            setShowWidgetControls(true);
                            setShowDashboardControls(false);
                          }
                        }}
                        className="h-8 px-3 gap-1.5"
                      >
                        {isEditing ? (
                          <>
                            <Check className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline">Done</span>
                          </>
                        ) : (
                          <>
                            <Pencil className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline">Edit</span>
                          </>
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {isEditing ? "Save changes" : "Edit dashboard layout"}
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
            )}
            {/* Three dots always visible */}
            {moreMenu}
          </div>
        </div>
      )}

      {/* Timeline subheader - centered above widgets - FIXED (hidden in skin mode, shown in header card) */}
      {(viewMode === 'grid') && currentGregorianDate && calendarWidgetIds.length > 0 && (
        <div className="flex-shrink-0 w-full text-center cursor-pointer py-3 border-b border-border/40 bg-background/20" onClick={handleCycleCalendarWidget}>
          <p className="text-base font-semibold text-muted-foreground hover:text-foreground transition-colors">
            {getCurrentCalendarDisplay()}
          </p>
        </div>
      )}

      {/* Grid Layout */}
      <ErrorBoundary onReset={() => { setIsEditing(false); setEditingWidgetId(null); setDesktopPagerSidebarOpen(false); }}>
        {/* Scrollable Widget Content Area */}
        <div className="overflow-hidden flex-1 min-h-0">
          <div id="dashboard-container" className="w-full h-full min-h-0" ref={containerRef}>
            {shouldShowLayoutSkeleton ? (
          <div className="rounded-2xl border border-border/60 bg-card/60 backdrop-blur p-6 space-y-6 animate-fade-in">
            {/* Header skeleton */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-muted/40 animate-pulse" />
                <div className="space-y-2">
                  <div className="h-4 w-32 rounded bg-muted/40 animate-pulse" />
                  <div className="h-3 w-24 rounded bg-muted/40 animate-pulse" />
                </div>
              </div>
              <div className="h-8 w-24 rounded-lg bg-muted/40 animate-pulse" />
            </div>
            
            {/* Grid skeleton */}
            <div className={isMobileLayout ? 'grid grid-cols-1 gap-4' : 'grid grid-cols-12 gap-4'}>
              {/* Large widget skeleton */}
              <div className={isMobileLayout ? '' : 'col-span-7 row-span-2'}>
                <div className="h-[280px] rounded-xl border border-border/40 bg-muted/20 animate-pulse flex items-center justify-center">
                  <div className="flex flex-col items-center gap-3 text-muted-foreground">
                    <svg className="h-8 w-8 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span className="text-sm">Loading dashboard layout…</span>
                  </div>
                </div>
              </div>
              
              {/* Side widgets skeleton */}
              <div className={isMobileLayout ? 'space-y-4' : 'col-span-5 space-y-4'}>
                <div className="h-[130px] rounded-xl border border-border/40 bg-muted/20 animate-pulse" />
                <div className="h-[130px] rounded-xl border border-border/40 bg-muted/20 animate-pulse" />
              </div>
              
              {/* Bottom widgets skeleton */}
              <div className={isMobileLayout ? 'h-[160px] rounded-xl border border-border/40 bg-muted/20 animate-pulse' : 'col-span-6 h-[160px] rounded-xl border border-border/40 bg-muted/20 animate-pulse'} />
              <div className={isMobileLayout ? 'h-[160px] rounded-xl border border-border/40 bg-muted/20 animate-pulse' : 'col-span-6 h-[160px] rounded-xl border border-border/40 bg-muted/20 animate-pulse'} />
            </div>
          </div>
        ) : isSkinMode ? (
          (() => {
            const SkinComponent = activeSkin.component as React.ComponentType<SkinProps>;
            // Filter out hidden widgets for skin mode
            const baseWidgets = widgets.filter(w => !skinHiddenWidgets.has(w.id));
            const hiddenSkinWidgets = widgets.filter(w => skinHiddenWidgets.has(w.id));
            const syncModes = buildSyncModes(widgetCustomizations);

            // Auto-hide waveforms when cycle rail is hidden
            const cyclesCustomization = widgetCustomizations?.['cycles'];
            const supportedWidgets = cyclesCustomization?.cycleRailHidden
              ? baseWidgets.filter(w => !['waveform', 'space-weather-waveforms'].includes(w.id))
              : baseWidgets;

            const globalSelectedCycleWidgetId = widgetCustomizations?.cycles?.selectedCycleWidgetId ?? selectedCycleWidgetId;
            const resolvedCycleWidgetId = supportedWidgets.some((w) => w.id === globalSelectedCycleWidgetId)
              ? globalSelectedCycleWidgetId
              : (supportedWidgets.find((w) => ['ai-day-summary', 'gregorian-grid', 'timeline', 'agenda'].includes(w.id))?.id ?? null);
            const cycleWidget = resolvedCycleWidgetId
              ? (widgets.find((w) => w.id === resolvedCycleWidgetId) || null)
              : null;

            return (
              <div className="relative h-full w-full">
                {/* Fullscreen skin container */}
                <SkinComponent
                  cycleWidget={cycleWidget}
                  currentDate={currentGregorianDate}
                  cycleRings={cycleRings}
                  hiddenRings={hiddenRings}
                  onToggleRing={onToggleRing}
                  onRingsChange={onRingsChange}
                  onRotationChange={onRotationChange}
                  onSyncToNow={onSyncToNow}
                  cycleDisplayMode={cycleDisplayMode}
                  onCycleDisplayModeChange={onCycleDisplayModeChange}
                  navItems={navItems}
                  onOpenTheme={onOpenTheme}
                  isAdmin={isEditable}
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                    widgets={supportedWidgets}
                    hiddenWidgets={hiddenSkinWidgets}
                    addableWidgets={addableWidgets}
                    onAddWidgetById={handleAddWidgetFromSkin}
                  selectedCycleWidgetId={globalSelectedCycleWidgetId}
                  onSelectCycleWidget={handleSelectCycleWidget}
                  onToggleWidgetVisibility={toggleWidgetVisibility}
                  onToggleWidgetSync={toggleWidgetSyncMode}
                  widgetSyncModes={syncModes}
                  widgetCustomizations={widgetCustomizations}
                  onWidgetCustomizationChange={handlePatchWidgetCustomization}
                  skinRowOrder={skinRowOrder}
                  onSkinRowOrderChange={setSkinRowOrder}
                  skinRowFractions={skinRowFractions}
                  onSkinRowFractionsCommit={setSkinRowFractions}
                  isNowLocked={isNowLocked}
                />
              </div>
            );
          })()
        ) : isPagerMode ? (
          (() => {
            const activeIndex = pagerWidgets.findIndex((widget) => widget.id === mobileActiveWidgetId);
            const resolvedIndex = activeIndex >= 0 ? activeIndex : 0;
            const activeWidget = pagerWidgets[resolvedIndex] ?? null;
            if (!activeWidget) return null;

            const resolvedComponent = resolveComponent(activeWidget.component);
            const componentElement = isValidElement(resolvedComponent) ? (resolvedComponent as React.ReactElement) : null;
            const toolbarActions = (componentElement?.props?.toolbarActions || activeWidget.toolbarActions) as {
              openWizard?: () => void;
              openTheme?: () => void;
              openAlignment?: (initial?: string) => void;
            } | undefined;

            const child = componentElement
              ? cloneElement(componentElement, componentElement.props)
              : resolvedComponent;

            const hasToolbar = Boolean(toolbarActions?.openAlignment || toolbarActions?.openTheme || toolbarActions?.openWizard);

            const goPrev = () => {
              if (pagerWidgets.length <= 1) return;
              const nextIndex = (resolvedIndex - 1 + pagerWidgets.length) % pagerWidgets.length;
              setMobileActiveWidgetId(pagerWidgets[nextIndex].id);
            };

            const goNext = () => {
              if (pagerWidgets.length <= 1) return;
              const nextIndex = (resolvedIndex + 1) % pagerWidgets.length;
              setMobileActiveWidgetId(pagerWidgets[nextIndex].id);
            };

            const handleCycleCalendarDisplay = () => {
              setCalendarDisplay((prev) => {
                if (prev === 'gregorian') return 'day13';
                if (prev === 'day13') return 'moonphase';
                return 'gregorian';
              });
            };

            const pagerNavOffsetPx = showPagerBottomNav ? MOBILE_PAGER_BOTTOM_NAV_HEIGHT_PX : 0;
            const mobilePagerCardStyle: React.CSSProperties | undefined = isCompactPagerLayout
              ? {
                  height: `calc(var(--app-height, 100dvh) - ${pagerNavOffsetPx}px - ${MOBILE_SAFE_AREA_BOTTOM})`,
                  display: 'flex',
                  flexDirection: 'column',
                }
              : {
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                };

            return (
              <>
                <div
                  className="flex flex-col overflow-hidden w-full"
                  style={mobilePagerCardStyle}
                >
                  {/* Unified subheader for both modes: only show widget title and icon here. On mobile, reduce padding and avoid extra space. */}
                  <div className={`flex items-center gap-2 min-w-0 border-b border-border/40 bg-background/20 ${isCompactPagerLayout ? 'px-3 py-2' : 'px-4 py-3'}`}>
                    {activeWidget.icon && <div className="text-primary shrink-0">{activeWidget.icon}</div>}
                    <p className="text-sm font-medium truncate">{getWidgetTitle(activeWidget)}</p>
                    {isEditable && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={
                          (widgetCustomizations[activeWidget.id]?.syncMode ?? 'linked') === 'linked'
                            ? 'Timeline linked'
                            : 'Timeline disconnected'
                        }
                        className={cn(
                          "h-7 w-7 hover:text-foreground",
                          (widgetCustomizations[activeWidget.id]?.syncMode ?? 'linked') === 'linked'
                            ? "text-emerald-500"
                            : "text-rose-500"
                        )}
                        onClick={() => toggleWidgetSyncMode(activeWidget.id)}
                      >
                        {(widgetCustomizations[activeWidget.id]?.syncMode ?? 'linked') === 'linked' ? (
                          <Link2 className="w-3.5 h-3.5" />
                        ) : (
                          <Link2Off className="w-3.5 h-3.5" />
                        )}
                      </Button>
                    )}
                    <div className="ml-auto flex items-center gap-2">
                      {isCompactPagerLayout && toolbarActions?.openAlignment && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => toolbarActions.openAlignment?.()}
                          title="Align cycles"
                        >
                          <Link2 className="w-4 h-4" />
                        </Button>
                      )}
                      {/* Pager navigation and sidebar toggle (desktop only) */}
                      {!isCompactPagerLayout && (
                        <>
                          <div className="text-xs text-muted-foreground shrink-0 mr-2">{`${resolvedIndex + 1}/${pagerWidgets.length}`}</div>
                          {pagerWidgets.length > 1 && (
                            <div className="flex items-center gap-1">
                              <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={goPrev} title="Previous widget">
                                <ChevronLeft className="w-4 h-4" />
                              </Button>
                              <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={goNext} title="Next widget">
                                <ChevronRight className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                          <Button
                            type="button"
                            variant={desktopPagerSidebarOpen ? 'default' : 'ghost'}
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => setDesktopPagerSidebarOpen((s) => !s)}
                            title={desktopPagerSidebarOpen ? 'Hide tools' : 'Show tools'}
                          >
                            {desktopPagerSidebarOpen ? <ChevronRight className="w-4 h-4 rotate-180" /> : <ChevronLeft className="w-4 h-4" />}
                          </Button>
                        </>
                      )}
                      {moreMenu}
                    </div>
                  </div>

                {isCompactPagerLayout ? (
                  <div
                    className="flex-1 min-h-0 overflow-auto p-3"
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'flex-start', height: '100%', boxSizing: 'border-box', paddingTop: '0.75rem' }}
                  >
                    {/* Timeline subheader in page mode */}
                    {activeWidget.id !== 'settings' && currentGregorianDate && (
                      <div
                        className="w-full text-center cursor-pointer mb-0"
                        onClick={handleCycleCalendarDisplay}
                      >
                        <p className="text-base font-semibold text-muted-foreground hover:text-foreground transition-colors">
                          {calendarDisplay === 'gregorian' && `${format(currentGregorianDate, 'EEE, MMM d')} • ${format(currentGregorianDate, 'HH:mm')}`}
                          {calendarDisplay === 'day13' && '13-Moon Calendar'}
                          {calendarDisplay === 'moonphase' && 'Moon Phase'}
                        </p>
                      </div>
                    )}
                    {activeWidget.id === 'settings' && mobilePagerSettingsHeader ? (
                      <div className="space-y-4 pb-4">
                        {mobilePagerSettingsHeader}
                      </div>
                    ) : null}
                    <div style={{ width: '100%' }}>
                      {child}
                    </div>
                  </div>
                ) : (
                  <div className="flex-1 min-h-0 overflow-hidden p-3">
                    {desktopPagerSidebarOpen ? (
                      <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-8 min-h-0 overflow-auto rounded-md bg-background/10 p-3 flex flex-col items-start justify-start" style={{height: '100%', boxSizing: 'border-box', paddingTop: '2.5rem'}}>
                          <div style={{ width: '100%' }}>
                            {child}
                          </div>
                        </div>

                        <aside className="col-span-4 min-h-0 overflow-auto rounded-md border-l border-border/30 pl-4 pr-2 py-2">
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-3 mb-2">
                                {activeWidget.icon && <div className="text-primary shrink-0">{activeWidget.icon}</div>}
                                <h4 className="text-sm font-medium">{getWidgetTitle(activeWidget)}</h4>
                              </div>
                              <p className="text-xs text-muted-foreground mb-3">Widget details and quick actions</p>
                            </div>
                          </div>

                          <div className="space-y-2">
                            {toolbarActions?.openAlignment && (
                              <Button type="button" variant="outline" size="sm" className="w-full" onClick={() => toolbarActions.openAlignment?.()}>
                                Align
                              </Button>
                            )}
                            {toolbarActions?.openTheme && (
                              <Button type="button" variant="outline" size="sm" className="w-full" onClick={() => toolbarActions.openTheme?.()}>
                                Theme
                              </Button>
                            )}
                            {toolbarActions?.openWizard && (
                              <Button type="button" variant="outline" size="sm" className="w-full" onClick={() => toolbarActions.openWizard?.()}>
                                Wizard
                              </Button>
                            )}
                          </div>

                          <div className="mt-4">
                            <h5 className="text-xs uppercase text-muted-foreground mb-2">Details</h5>
                            <pre className="text-xs bg-muted/10 rounded p-2 overflow-auto max-h-40 text-[11px]">{JSON.stringify({ id: activeWidget.id, title: activeWidget.title }, null, 2)}</pre>
                          </div>

                          <div className="mt-4">
                            <h5 className="text-xs uppercase text-muted-foreground mb-2">Edit</h5>
                            <div className="space-y-2">
                              <Button type="button" size="sm" variant="ghost" className="w-full" onClick={() => setEditingWidgetId(activeWidget.id)}>Open editor</Button>
                              <Button type="button" size="sm" variant="ghost" className="w-full">Customize</Button>
                            </div>
                          </div>
                        </aside>
                      </div>
                    ) : (
                      <div className="min-h-0 overflow-auto p-2">{child}</div>
                    )}
                  </div>
                )}

                {!isCompactPagerLayout && hasToolbar ? (
                  <div className="border-t border-border/40 bg-background/30 backdrop-blur">
                    <div className="flex items-center justify-center gap-2 px-2 py-3">
                      {toolbarActions?.openAlignment ? (
                        <Button type="button" variant="outline" size="sm" className="gap-2" onClick={() => toolbarActions.openAlignment?.()}>
                          <Link2 className="w-4 h-4" />
                          Align
                        </Button>
                      ) : null}
                      {toolbarActions?.openTheme ? (
                        <Button type="button" variant="outline" size="sm" className="gap-2" onClick={() => toolbarActions.openTheme?.()}>
                          <Pencil className="w-4 h-4" />
                          Theme
                        </Button>
                      ) : null}
                      {toolbarActions?.openWizard ? (
                        <Button type="button" variant="outline" size="sm" className="gap-2" onClick={() => toolbarActions.openWizard?.()}>
                          <Layers className="w-4 h-4" />
                          Wizard
                        </Button>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>
            </>
            );
          })()
        ) : viewMode === 'grid' ? (
          // Grid Mode with GridLayout
          <div
            className={cn(
              "w-full",
              isMobileLayout && "pb-[calc(5rem+var(--safe-area-bottom,0px))]"
            )}
            style={isMobileLayout ? { minHeight: 'var(--app-height, 100dvh)' } : undefined}
          >
            <GridLayout
              className={cn("layout", isEditing && "dashboard-editing")}
              layout={displayLayout}
              cols={COLS}
              rowHeight={ROW_HEIGHT}
              width={effectiveContainerWidth}
              onLayoutChange={handleLayoutChange}
              onDragStop={handleLayoutStop}
              onResizeStop={handleLayoutStop}
              draggableHandle=".widget-drag-handle"
              draggableCancel=".widget-action, .widget-toolbar, .tooltip-trigger"
              isDraggable={isEditing}
              isResizable={isEditing}
              compactType="vertical"
              preventCollision={false}
              useCSSTransforms={!suppressTransitions}
              resizeHandles={['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw']}
            >
            {widgets.filter(w => !gridHiddenWidgets.has(w.id)).map((widget) => {
              const variant = resolvePagerGridVariant(widget);
              const rawNode = variant === 'short' && widget.shortComponent ? widget.shortComponent : widget.component;
              const widgetNode = resolveComponent(rawNode);
              const componentElement = isValidElement(widgetNode) ? (widgetNode as React.ReactElement) : null;

              const toolbarActions = componentElement?.props?.toolbarActions as {
                openWizard?: () => void;
                openTheme?: () => void;
                openAlignment?: (initial?: string) => void;
              } | undefined;

              // Get current layout dimensions for this widget
              const currentLayoutItem = displayLayout?.find(item => item.i === widget.id);
              const widgetSize = currentLayoutItem 
                ? { width: currentLayoutItem.w, height: currentLayoutItem.h }
                : { width: widget.defaultLayout.w, height: widget.defaultLayout.h };

              const extraActions = isEditing && toolbarActions ? (
                <div className="flex items-center gap-1">
                  {toolbarActions.openWizard && (
                    <Tooltip key="ta-wizard">
                      <TooltipTrigger asChild>
                        <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => toolbarActions.openWizard?.() }>
                          <Settings2 className="w-3 h-3" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Open cycle wizard</TooltipContent>
                    </Tooltip>
                  )}
                  {toolbarActions.openTheme && (
                    <Tooltip key="ta-theme">
                      <TooltipTrigger asChild>
                        <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => toolbarActions.openTheme?.() }>
                          <Palette className="w-3 h-3" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Edit ring theme</TooltipContent>
                    </Tooltip>
                  )}
                  {toolbarActions.openAlignment && (
                    <Tooltip key="ta-link">
                      <TooltipTrigger asChild>
                        <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => toolbarActions.openAlignment?.() }>
                          <Link2 className="w-3 h-3" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Align cycles</TooltipContent>
                    </Tooltip>
                  )}
                </div>
              ) : undefined;

              const child = componentElement
                ? cloneElement(componentElement, { 
                    widgetSize: widgetSize,
                    ...componentElement.props,
                  })
                : widgetNode;

              return (
                <div key={widget.id} data-grid-item-id={widget.id}>
                  <DashboardWidget
                    title={getWidgetTitle(widget)}
                    icon={widget.icon}
                    onRemove={isEditing && onRemoveWidget ? () => onRemoveWidget(widget.id) : undefined}
                    onEdit={isEditing ? () => setEditingWidgetId(widget.id) : undefined}
                    isEditing={isEditing}
                    isMinimized={minimizedWidgets.has(widget.id)}
                    syncMode={widgetCustomizations[widget.id]?.syncMode ?? 'linked'}
                    fixedTimestamp={widgetCustomizations[widget.id]?.fixedTimestamp}
                    showFixedBadge={showPrivilegedFixedSyncUi}
                    onToggleSync={isEditable ? () => toggleWidgetSyncMode(widget.id) : undefined}
                    onToggleMinimize={
                      isEditable || minimizedWidgets.has(widget.id)
                        ? (min) => handleToggleMinimize(widget.id, min)
                        : undefined
                    }
                    onHide={
                      !isEditing && isEditable && onRemoveWidget
                        ? () => onRemoveWidget(widget.id)
                        : undefined
                    }
                    extraActions={
                      <>
                        {extraActions}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              aria-label="Expand to fill dashboard"
                              onClick={() => setMaximizedWidgetId(widget.id)}
                            >
                              <PanelsTopLeft className="w-4 h-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Expand to fill dashboard</TooltipContent>
                        </Tooltip>
                      </>
                    }
                  >
                    {child}
                  </DashboardWidget>
                </div>
              );
            })}
          </GridLayout>
          </div>
        ) : (
          // Slider Mode (fallback - shouldn't normally be reached)
          <div className="w-full h-full overflow-hidden flex flex-col">
            {/* Horizontal Slider Container */}
            <div 
              ref={sliderScrollRef}
              className="flex-1 overflow-x-auto overflow-y-hidden snap-x snap-mandatory scroll-smooth"
              style={{ scrollBehavior: 'smooth' }}
            >
              <div className="flex h-full">
                {widgets.filter(w => w && w.component && !skinHiddenWidgets.has(w.id)).map((widget, index) => {
              const variant = resolvePagerGridVariant(widget);
              const rawNode = variant === 'short' && widget.shortComponent ? widget.shortComponent : widget.component;
              const widgetNode = resolveComponent(rawNode);
              const componentElement = isValidElement(widgetNode) ? (widgetNode as React.ReactElement) : null;
              const toolbarActions = componentElement?.props?.toolbarActions as {
                openWizard?: () => void;
                openTheme?: () => void;
                openAlignment?: (initial?: string) => void;
              } | undefined;

              // Get current layout dimensions for this widget
              const currentLayoutItem = displayLayout?.find(item => item.i === widget.id);
              const widgetSize = currentLayoutItem 
                ? { width: currentLayoutItem.w, height: currentLayoutItem.h }
                : { width: widget.defaultLayout.w, height: widget.defaultLayout.h };

              const extraActions = isEditing && toolbarActions ? (
                <div className="flex items-center gap-1">
                  {toolbarActions.openWizard && (
                    <Tooltip key="ta-wizard">
                      <TooltipTrigger asChild>
                        <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => toolbarActions.openWizard?.() }>
                          <Settings2 className="w-3 h-3" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Open cycle wizard</TooltipContent>
                    </Tooltip>
                  )}
                  {toolbarActions.openTheme && (
                    <Tooltip key="ta-theme">
                      <TooltipTrigger asChild>
                        <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => toolbarActions.openTheme?.() }>
                          <Palette className="w-3 h-3" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Edit ring theme</TooltipContent>
                    </Tooltip>
                  )}
                  {toolbarActions.openAlignment && (
                    <Tooltip key="ta-link">
                      <TooltipTrigger asChild>
                        <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => toolbarActions.openAlignment?.() }>
                          <Link2 className="w-3 h-3" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Align cycles</TooltipContent>
                    </Tooltip>
                  )}
                </div>
              ) : undefined;

              const child = componentElement
                ? cloneElement(componentElement, { 
                    widgetSize: widgetSize,
                    ...componentElement.props,
                  })
                : widgetNode;

              return (
                <div 
                  key={widget.id} 
                  data-grid-item-id={widget.id}
                  className="flex-shrink-0 h-full snap-start snap-always"
                  style={{ 
                    width: '100vw',
                    minWidth: '100vw',
                    maxWidth: '100vw'
                  }}
                >
                  <div className="w-full h-full flex items-center justify-center p-4">
                    <div className="w-full h-full max-w-full overflow-auto">
                    <DashboardWidget
                      title={getWidgetTitle(widget)}
                      icon={widget.icon}
                      onRemove={isEditing && onRemoveWidget ? () => onRemoveWidget(widget.id) : undefined}
                      onEdit={isEditing ? () => setEditingWidgetId(widget.id) : undefined}
                      isEditing={isEditing}
                    isMinimized={minimizedWidgets.has(widget.id)}
                    syncMode={widgetCustomizations[widget.id]?.syncMode ?? 'linked'}
                      fixedTimestamp={widgetCustomizations[widget.id]?.fixedTimestamp}
                      showFixedBadge={showPrivilegedFixedSyncUi}
                    onToggleSync={isEditable ? () => toggleWidgetSyncMode(widget.id) : undefined}
                    onToggleMinimize={
                      isEditable || minimizedWidgets.has(widget.id)
                        ? (min) => handleToggleMinimize(widget.id, min)
                        : undefined
                    }
                    onHide={
                      !isEditing && isEditable && onRemoveWidget
                        ? () => onRemoveWidget(widget.id)
                        : undefined
                    }
                    extraActions={
                      <>
                        {extraActions}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              aria-label="Expand to fill dashboard"
                              onClick={() => setMaximizedWidgetId(widget.id)}
                            >
                              <PanelsTopLeft className="w-4 h-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Expand to fill dashboard</TooltipContent>
                        </Tooltip>
                      </>
                    }
                  >
                    {child}
                  </DashboardWidget>
                    </div>
                  </div>
                </div>
              );
            })}
              </div>
            </div>
          </div>
        )}
          </div>
        </div>
      </ErrorBoundary>

      {/* Mobile Pager Bottom Nav - Fixed */}
      {showPagerBottomNav && (
        <nav
          data-testid="mobile-dashboard-bottom-nav"
          className="fixed bottom-0 left-0 right-0 z-50 border-t border-border/60 bg-background/90 backdrop-blur supports-[backdrop-filter]:bg-background/70"
        >
          <div className="mx-auto max-w-screen-md px-2" style={{ paddingBottom: `calc(${MOBILE_SAFE_AREA_BOTTOM})` }}>
            <div
              className="grid"
              style={{
                gridTemplateColumns: `repeat(${Math.max(1, pagerWidgets.length)}, minmax(0, 1fr))`,
              }}
            >
              {pagerWidgets.map((widget, index) => {
                const activeIndex = pagerWidgets.findIndex((w) => w.id === mobileActiveWidgetId);
                const resolvedIndex = activeIndex >= 0 ? activeIndex : 0;
                const selected = index === resolvedIndex;
                const title = getWidgetTitle(widget);
                return (
                  <Button
                    key={widget.id}
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setMobileActiveWidgetId(widget.id)}
                    className={`h-12 flex-col gap-1 rounded-none ${
                      selected ? 'text-foreground' : 'text-muted-foreground'
                    }`}
                    aria-current={selected ? 'page' : undefined}
                    title={title}
                  >
                    <div className="flex items-center justify-center">
                      {widget.icon ?? <PanelsTopLeft className="h-4 w-4" />}
                    </div>
                    <span className="text-[10px] leading-3 tracking-wide truncate max-w-full">{title}</span>
                    <span
                      className={
                        selected
                          ? 'h-[2px] w-6 rounded-full bg-foreground/70'
                          : 'h-[2px] w-6 rounded-full bg-transparent'
                      }
                    />
                  </Button>
                );
              })}
            </div>
          </div>
        </nav>
      )}

      {/* Widget Editor Modal */}
      {editingWidget && (
        <WidgetEditor
          open={!!editingWidgetId}
          onClose={() => setEditingWidgetId(null)}
          widgetId={editingWidget.id}
          currentTitle={editingWidget.title}
          customizations={widgetCustomizations}
          onSave={handleSaveCustomization}
          onReset={handleResetCustomization}
        />
      )}

      {/* Maximized Widget Overlay */}
      {maximizedWidgetId && (() => {
        const widget = widgets.find(w => w.id === maximizedWidgetId);
        if (!widget) return null;
        return (
          <div 
            className="fixed inset-0 z-50 bg-background/95 backdrop-blur-md flex flex-col"
            onClick={() => setMaximizedWidgetId(null)}
          >
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center gap-3">
                {widget.icon && <div className="text-primary">{widget.icon}</div>}
                <h2 className="text-lg font-semibold">{getWidgetTitle(widget)}</h2>
              </div>
              {!shouldForceMaximize && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => setMaximizedWidgetId(null)}
                      aria-label="Return to normal size"
                    >
                      <X className="w-5 h-5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Return to normal size</TooltipContent>
                </Tooltip>
              )}
            </div>
            <div 
              className="flex-1 overflow-auto p-6"
              onClick={(e) => e.stopPropagation()}
            >
              {resolveComponent(widget.component)}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
